import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useT } from "../i18n";

/**
 * SignInNotice says why a sign-in did not happen.
 *
 * A failed sign-in is a browser redirect to `/?signin=<reason>` and not a
 * problem+json document, because this URL is opened by a browser following a
 * redirect from the provider (docs/05-api-and-clients.md). Until ADR-0084
 * nothing read the parameter at all, so all four reasons — a cancelled consent
 * screen, an expired one, a disabled account, an outright failure — rendered as
 * an ordinary signed-out page: the system decided something and said nothing.
 * ADR-0084 adds a fifth, `conflict`, and a fifth silent reason would mean
 * refusing somebody and telling them nothing.
 *
 * The parameter is cleared once shown, so a reload or a shared URL does not
 * re-accuse the visitor of something that happened once — but the *reason* is
 * latched on the first render and kept. Clearing the URL without latching made
 * the message appear for a single tick and vanish, which is the silence this
 * component exists to end wearing a different hat.
 */
export default function SignInNotice() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [reason] = useState(() => params.get("signin"));

  useEffect(() => {
    if (!reason) return;
    const next = new URLSearchParams(window.location.search);
    next.delete("signin");
    setParams(next, { replace: true });
    // Once, on mount. `params` is deliberately not a dependency: the effect
    // changes it, so depending on it would re-run this on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason]);

  if (!reason) return null;

  // The default is required rather than tidy (ADR-0084): nothing checks that
  // the Go vocabulary and these keys agree, so a reason added on the server
  // without a message here must degrade to a vague refusal. Degrading to a
  // vague sentence is acceptable; degrading back to silence is the defect this
  // component exists to end.
  const reasons = t.signIn.reasons as Record<string, string>;
  const message = reasons[reason] ?? reasons.failed;

  // Its own column and gutter: it sits above the routes, outside any page's
  // <main>, so it reproduces the width and the 16px inset the pages use rather
  // than running to the edges of a phone.
  return (
    <div className="mx-auto max-w-5xl px-4 pt-4">
      <p
        role="alert"
        data-testid="signin-notice"
        dir="auto"
        className="rounded-card border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
      >
        {message}
      </p>
    </div>
  );
}
